'use client';
import React, { useState, FormEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  GraduationCap, HeartPulse, Leaf, CheckCircle, Shield, CreditCard, Lock,
  RefreshCw, ArrowRight, ArrowLeft, Heart, Clock, Info, Gift,
} from 'lucide-react';
import { toast } from 'sonner';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import DonationConfirmation from './DonationConfirmation';
import { trackDonationStart, trackCauseSelected, trackAmountSelected, trackDonorDetailsSubmitted, trackPaymentInitiated, trackDonationComplete,  } from '@/lib/analytics';

// ─── Types ───────────────────────────────────────────────────────────────────
type Cause = 'education' | 'health' | 'environment';
type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface DonorForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  giftAid: boolean;
}

interface DonorData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  giftAid: boolean;
}

// ─── Static Data ─────────────────────────────────────────────────────────────
const causes = [
  {
    id: 'education' as Cause,
    icon: GraduationCap,
    title: 'Educational Support',
    short: 'Education',
    description: 'Fund scholarships, classrooms, and teacher training for underserved communities.',
    impact: 'Your $50/month funds one student\'s supplies for a full academic year.',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary',
  },
  {
    id: 'health' as Cause,
    icon: HeartPulse,
    title: 'Health & Care Support',
    short: 'Health',
    description: 'Provide medical equipment, mobile clinics, and clean water to rural populations.',
    impact: 'Your $50/month covers one patient\'s mobile clinic consultation and medication.',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-400',
  },
  {
    id: 'environment' as Cause,
    icon: Leaf,
    title: 'Environmental Support',
    short: 'Environment',
    description: 'Plant trees, restore wetlands, and power villages with clean renewable energy.',
    impact: 'Your $50/month plants 25 trees and funds climate education for 10 youth.',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent',
  },
];

const frequencies: { id: Frequency; label: string; badge?: string }[] = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly', badge: 'Most Popular' },
  { id: 'yearly', label: 'Yearly', badge: 'Best Value' },
];

const presetAmounts = [10, 25, 50, 100, 250, 500];

const frequencyMultipliers: Record<Frequency, number> = {
  daily: 365,
  weekly: 52,
  monthly: 12,
  yearly: 1,
};

const frequencyLabels: Record<Frequency, string> = {
  daily: 'per day',
  weekly: 'per week',
  monthly: 'per month',
  yearly: 'per year',
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function StepIndicator({ step }: { step: number }) {
  const steps = ['Choose Cause', 'Frequency & Amount', 'Your Details', 'Payment'];
  return (
    <div className="flex items-center gap-0 mb-10">
      {steps.map((label, idx) => (
        <React.Fragment key={`step-indicator-${idx}`}>
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-200 ${
                idx < step
                  ? 'bg-primary border-primary text-white'
                  : idx === step
                  ? 'bg-primary border-primary text-white shadow-primary-glow'
                  : 'bg-white border-border text-muted-foreground'
              }`}
            >
              {idx < step ? <CheckCircle size={16} /> : idx + 1}
            </div>
            <span
              className={`text-xs mt-1.5 font-medium hidden sm:block ${
                idx === step ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${
                idx < step ? 'bg-primary' : 'bg-border'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function DonationSummary({
  cause,
  frequency,
  amount,
}: {
  cause: Cause | null;
  frequency: Frequency;
  amount: number;
}) {
  const causeData = causes.find((c) => c.id === cause);
  const annualTotal = amount * frequencyMultipliers[frequency];

  return (
    <div className="bg-card rounded-2xl border border-border shadow-card p-6 sticky top-28">
      <h3 className="font-bold text-foreground text-base mb-5 flex items-center gap-2">
        <Heart size={16} className="text-primary" />
        Donation Summary
      </h3>

      {cause ? (
        <>
          <div className="space-y-4 mb-5">
            <div className="flex justify-between items-start text-sm">
              <span className="text-muted-foreground">Cause</span>
              <span className="font-semibold text-foreground text-right max-w-[140px]">
                {causeData?.title}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Frequency</span>
              <span className="font-semibold text-foreground capitalize">{frequency}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-bold text-primary text-base font-tabular">
                ${amount > 0 ? amount.toFixed(2) : '0.00'} {frequencyLabels[frequency]}
              </span>
            </div>
          </div>

          <div className="section-divider mb-5" />

          <div className="bg-primary/6 rounded-xl p-4 mb-5">
            <div className="text-xs text-muted-foreground mb-1">Projected Annual Impact</div>
            <div className="text-2xl font-bold text-primary font-tabular">
              ${annualTotal > 0 ? annualTotal.toLocaleString() : '0'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">total per year</div>
          </div>

          {causeData && amount > 0 && (
            <div className="bg-accent/8 rounded-xl p-4 mb-5 flex gap-2">
              <Info size={14} className="text-accent shrink-0 mt-0.5" />
              <p className="text-xs text-secondary-foreground leading-relaxed">
                {causeData.impact}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <Heart size={32} className="text-muted mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Select a cause to see your impact summary.</p>
        </div>
      )}

      {/* Trust Badges */}
      <div className="space-y-2 pt-4 border-t border-border">
        {[
          { icon: Lock, text: 'SSL 256-bit encrypted' },
          { icon: Shield, text: 'Stripe-secured payments' },
          { icon: CheckCircle, text: '100% to your chosen cause' },
        ].map(({ icon, text }) => (
          <div key={`trust-summary-${text}`} className="flex items-center gap-2">
            {React.createElement(icon, { size: 13, className: "text-accent shrink-0" })}
            <span className="text-xs text-muted-foreground">{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Stripe Payment Form ──────────────────────────────────────────────────────
interface StripePaymentFormProps {
  clientSecret: string;
  amount: number;
  frequency: Frequency;
  cause: string;
  causeTitle: string;
  donorName: string;
  donorEmail: string;
  onSuccess: (paymentIntent: PaymentIntent) => void;
  onBack: () => void;
}

function StripePaymentForm({ clientSecret, amount, frequency, cause, causeTitle, donorName, donorEmail, onSuccess, onBack }: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setPaymentError(null);

    const returnParams = new URLSearchParams({
      amount: amount.toString(),
      cause,
      causeTitle,
      frequency,
      name: donorName,
      email: donorEmail,
    });

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/donation-screen/success?${returnParams.toString()}`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setPaymentError(error.message ?? 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent);
    } else if (paymentIntent?.status === 'requires_action') {
      // 3D Secure handled by Stripe — redirect will occur
      setIsProcessing(false);
    } else {
      setPaymentError('Payment could not be completed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-card rounded-2xl border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
            <CreditCard size={16} className="text-primary" />
            Card Details
          </h3>
          <div className="flex items-center gap-2">
            {['VISA', 'MC', 'AMEX'].map((brand) => (
              <span
                key={`card-brand-${brand}`}
                className="text-xs font-bold bg-muted text-muted-foreground px-2 py-0.5 rounded"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>

        {/* Stripe PaymentElement — renders real card input */}
        <PaymentElement
          options={{
            layout: 'tabs',
            fields: {
              billingDetails: {
                address: 'never',
              },
            },
          }}
        />

        {paymentError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {paymentError}
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="trust-badge rounded-xl p-4 mb-7 flex gap-3">
        <Lock size={15} className="text-primary shrink-0 mt-0.5" />
        <div>
          <div className="text-xs font-semibold text-foreground mb-0.5">Secure & Encrypted</div>
          <div className="text-xs text-muted-foreground">
            Your payment is processed by Stripe — PCI DSS Level 1 certified. MOURNDIECE Foundation never stores your card details.
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-5 py-3 rounded-xl border border-border hover:border-primary/30 transition-all disabled:opacity-50"
        >
          <ArrowLeft size={15} />
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || !elements || isProcessing}
          className="flex items-center gap-2 btn-primary font-bold text-sm px-8 py-3.5 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed min-w-[200px] justify-center"
        >
          {isProcessing ? (
            <>
              <RefreshCw size={15} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Lock size={15} />
              Donate ${amount.toFixed(2)} {frequencyLabels[frequency]}
            </>
          )}
        </button>
      </div>

      <p className="text-xs text-muted-foreground text-center mt-5">
        By donating, you agree to our{' '}
        <a href="#" className="text-primary hover:underline">Terms of Service</a>{' '}
        and{' '}
        <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
      </p>
    </form>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DonationFlow() {
  const [step, setStep] = useState(0);
  const [selectedCause, setSelectedCause] = useState<Cause | null>(null);
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [completedPayment, setCompletedPayment] = useState<PaymentIntent | null>(null);
  const [donorData, setDonorData] = useState<DonorData | null>(null);
  const [presetAmountsLoaded, setPresetAmountsLoaded] = useState([10, 25, 50, 100, 250, 500]);

  const supabase = createClient();

  // Track donation funnel start
  useEffect(() => {
    trackDonationStart();
  }, []);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        const { data } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'donation_preset_amounts')
          .single();
        if (data?.value) {
          const parsed = data.value
            .split(',')
            .map((v: string) => parseInt(v.trim(), 10))
            .filter((n: number) => !isNaN(n) && n > 0);
          if (parsed.length > 0) {
            setPresetAmountsLoaded(parsed);
            setAmount(parsed[2] ?? parsed[0]);
          }
        }
      } catch {
        // fallback to defaults
      }
    };
    loadPresets();
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<DonorForm>();

  const effectiveAmount = isCustom ? parseFloat(customAmount) || 0 : amount;

  const handleCauseSelect = (id: Cause) => {
    setSelectedCause(id);
    const causeData = causes.find((c) => c.id === id);
    trackCauseSelected(causeData?.title ?? id);
  };

  const handleAmountSelect = (val: number) => {
    setAmount(val);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomAmount = (val: string) => {
    setCustomAmount(val);
    setIsCustom(true);
  };

  const nextStep = () => {
    if (step === 0 && !selectedCause) {
      toast.error('Please select a cause to continue.');
      return;
    }
    if (step === 1 && effectiveAmount < 1) {
      toast.error('Please enter a donation amount of at least $1.');
      return;
    }
    // Track step progression
    if (step === 1) {
      trackAmountSelected(effectiveAmount, frequency);
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const prevStep = () => {
    if (step === 3) {
      setClientSecret(null);
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  // Called when donor details form is submitted — creates payment intent
  const onDonorDetailsSubmit = async (data: DonorForm) => {
    setDonorData(data);
    setIsLoadingIntent(true);
    trackDonorDetailsSubmitted();

    try {
      const isOneTime = frequency === 'daily';
      const res = await fetch('/api/donations/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: effectiveAmount,
          currency: 'usd',
          frequency,
          cause: selectedCause,
          isOneTime,
          donorInfo: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            message: data.message,
          },
        }),
      });

      const result = await res.json();

      if (!res.ok || result.error) {
        toast.error(result.error ?? 'Failed to initialize payment. Please try again.');
        setIsLoadingIntent(false);
        return;
      }

      setClientSecret(result.clientSecret);
      trackPaymentInitiated(effectiveAmount, selectedCause ?? '', frequency);
      setStep(3);
    } catch {
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setIsLoadingIntent(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent: PaymentIntent) => {
    toast.success('Donation confirmed! Receipt sent to your email.');
    const causeData = causes.find((c) => c.id === selectedCause);
    trackDonationComplete(effectiveAmount, causeData?.title ?? selectedCause ?? '', frequency, paymentIntent.id);
    const params = new URLSearchParams({
      payment_intent: paymentIntent.id ?? '',
      amount: effectiveAmount.toString(),
      cause: selectedCause ?? '',
      causeTitle: causeData?.title ?? '',
      frequency,
      name: donorData ? `${donorData.firstName} ${donorData.lastName}` : '',
      email: donorData?.email ?? '',
    });
    window.location.href = `/donation-screen/success?${params.toString()}`;
  };

  if (isComplete) {
    const causeData = causes.find((c) => c.id === selectedCause);
    return (
      <DonationConfirmation
        paymentIntent={completedPayment!}
        donorData={donorData!}
        cause={selectedCause ?? ''}
        causeTitle={causeData?.title ?? ''}
        frequency={frequency}
        amount={effectiveAmount}
      />
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-10 lg:py-14">
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-10 items-start">
        {/* Main Form */}
        <div className="xl:col-span-2">
          <StepIndicator step={step} />

          {/* Step 0 — Choose Cause */}
          {step === 0 && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-2">Which cause would you like to support?</h2>
              <p className="text-muted-foreground text-sm mb-7">
                100% of your donation goes directly to your selected cause.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                {causes.map((cause) => {
                  const CauseIcon = cause.icon;
                  const isSelected = selectedCause === cause.id;
                  return (
                    <button
                      key={`cause-select-${cause.id}`}
                      type="button"
                      onClick={() => handleCauseSelect(cause.id)}
                      className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 bg-card ${
                        isSelected
                          ? `cause-card-selected ${cause.border}`
                          : 'border-border hover:border-primary/30 hover:shadow-card'
                      }`}
                    >
                      <div className={`w-11 h-11 ${cause.bg} rounded-xl flex items-center justify-center mb-4`}>
                        <CauseIcon size={20} className={cause.color} />
                      </div>
                      <h3 className="font-bold text-foreground text-sm mb-2">{cause.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-4">{cause.description}</p>
                      {isSelected && (
                        <div className="mt-1 flex items-center gap-1.5 text-primary text-xs font-semibold">
                          <CheckCircle size={13} />
                          Selected
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 btn-primary font-semibold text-sm px-7 py-3.5 rounded-xl"
                >
                  Continue
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Step 1 — Frequency & Amount */}
          {step === 1 && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-2">How often would you like to give?</h2>
              <p className="text-muted-foreground text-sm mb-7">
                Recurring donations allow us to plan long-term programs and maximize impact.
              </p>

              {/* Frequency Tabs */}
              <div className="flex gap-2 mb-8 flex-wrap">
                {frequencies.map((f) => (
                  <button
                    key={`freq-${f.id}`}
                    type="button"
                    onClick={() => setFrequency(f.id)}
                    className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl border font-semibold text-sm transition-all duration-150 ${
                      frequency === f.id
                        ? 'bg-primary text-white border-primary shadow-primary-glow'
                        : 'bg-card text-secondary-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    {f.id === 'monthly' || f.id === 'yearly' ? (
                      <RefreshCw size={13} />
                    ) : (
                      <Clock size={13} />
                    )}
                    {f.label}
                    {f.badge && (
                      <span
                        className={`absolute -top-2.5 -right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          f.id === 'yearly' ? 'bg-accent text-white' : 'bg-primary-light text-white'
                        }`}
                      >
                        {f.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Amount Selector */}
              <div className="mb-8">
                <label className="text-sm font-semibold text-foreground mb-4 block">
                  Select or enter an amount ({frequencyLabels[frequency]})
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
                  {presetAmountsLoaded.map((val) => (
                    <button
                      key={`amount-${val}`}
                      type="button"
                      onClick={() => handleAmountSelect(val)}
                      className={`py-3 rounded-xl border font-bold text-sm font-tabular transition-all duration-150 ${
                        !isCustom && amount === val
                          ? 'amount-btn-selected shadow-primary-glow'
                          : 'bg-card text-foreground border-border hover:border-primary/40'
                      }`}
                    >
                      ${val}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold text-sm">$</span>
                  <input
                    type="number"
                    min="1"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => handleCustomAmount(e.target.value)}
                    className={`w-full pl-8 pr-4 py-3.5 rounded-xl border text-sm font-semibold font-tabular bg-card focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                      isCustom ? 'border-primary' : 'border-border'
                    }`}
                  />
                </div>
              </div>

              {/* Frequency Callout */}
              {frequency === 'yearly' && (
                <div className="bg-accent/8 border border-accent/20 rounded-xl p-4 mb-7 flex gap-3">
                  <Gift size={16} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-foreground mb-1">Annual giving saves admin costs</div>
                    <div className="text-xs text-muted-foreground">
                      A single yearly payment reduces processing fees and administrative overhead, meaning more of your donation reaches the cause directly.
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-5 py-3 rounded-xl border border-border hover:border-primary/30 transition-all"
                >
                  <ArrowLeft size={15} />
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 btn-primary font-semibold text-sm px-7 py-3.5 rounded-xl"
                >
                  Continue
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — Donor Details */}
          {step === 2 && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-2">Tell us about yourself</h2>
              <p className="text-muted-foreground text-sm mb-7">
                Your details are used to send your receipt and impact reports. We never sell your data.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    placeholder="Amara"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  {errors.firstName && (
                    <p className="text-danger text-xs mt-1.5">{errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    placeholder="Osei"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  {errors.lastName && (
                    <p className="text-danger text-xs mt-1.5">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Email Address <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">Your receipt and impact reports will be sent here.</p>
                <input
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                  })}
                  placeholder="amara@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                {errors.email && (
                  <p className="text-danger text-xs mt-1.5">{errors.email.message}</p>
                )}
              </div>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
                <p className="text-xs text-muted-foreground mb-2">Optional — for donation confirmation SMS.</p>
                <input
                  type="tel"
                  {...register('phone')}
                  placeholder="+1 (212) 555-0193"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-1.5">Message (Optional)</label>
                <p className="text-xs text-muted-foreground mb-2">Leave a note of encouragement for the team and beneficiaries.</p>
                <textarea
                  {...register('message')}
                  rows={3}
                  placeholder="I'm donating in memory of my father who believed in education for all..."
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                />
              </div>

              {/* Gift Aid */}
              <div className="bg-card border border-border rounded-xl p-5 mb-7 flex gap-3">
                <input
                  type="checkbox"
                  id="gift-aid"
                  {...register('giftAid')}
                  className="w-4 h-4 mt-0.5 rounded accent-primary shrink-0"
                />
                <div>
                  <label htmlFor="gift-aid" className="text-sm font-semibold text-foreground cursor-pointer">
                    Add Gift Aid (UK taxpayers only)
                  </label>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    If you are a UK taxpayer, we can claim an extra 25p for every £1 you donate at no extra cost to you. Your donation must qualify for Gift Aid.
                  </p>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-5 py-3 rounded-xl border border-border hover:border-primary/30 transition-all"
                >
                  <ArrowLeft size={15} />
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      selectedCause === 'education' &&
                      frequency === 'monthly' &&
                      effectiveAmount === 50
                    ) {
                      window.open(
                        'https://pay.mourndiecefoundation.org/b/fZucMYej31XdflZgGXawo00',
                        '_blank'
                      );
                      return;
                    }
                    handleSubmit(onDonorDetailsSubmit)();
                  }}
                  disabled={isLoadingIntent}
                  className="flex items-center gap-2 btn-primary font-semibold text-sm px-7 py-3.5 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoadingIntent ? (
                    <>
                      <RefreshCw size={15} className="animate-spin" />
                      Preparing Payment...
                    </>
                  ) : (
                    <>
                      Continue to Payment
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — Payment (Stripe Elements) */}
          {step === 3 && clientSecret && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-2">Secure Payment</h2>
              <p className="text-muted-foreground text-sm mb-7">
                Your payment is encrypted and processed securely via Stripe. We never store your card details.
              </p>

              <Elements
                stripe={getStripe()}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#2563eb',
                      borderRadius: '12px',
                      fontFamily: 'inherit',
                    },
                  },
                }}
              >
                <StripePaymentForm
                  clientSecret={clientSecret}
                  amount={effectiveAmount}
                  frequency={frequency}
                  cause={selectedCause ?? ''}
                  causeTitle={causes.find((c) => c.id === selectedCause)?.title ?? ''}
                  donorName={donorData ? `${donorData.firstName} ${donorData.lastName}` : ''}
                  donorEmail={donorData?.email ?? ''}
                  onSuccess={handlePaymentSuccess}
                  onBack={prevStep}
                />
              </Elements>
            </div>
          )}

          {/* Step 3 loading state (while fetching client secret) */}
          {step === 3 && !clientSecret && (
            <div className="animate-fade-in flex flex-col items-center justify-center py-20">
              <RefreshCw size={32} className="animate-spin text-primary mb-4" />
              <p className="text-muted-foreground text-sm">Preparing your secure payment...</p>
            </div>
          )}
        </div>

        {/* Sidebar Summary */}
        <div className="xl:col-span-1">
          <DonationSummary cause={selectedCause} frequency={frequency} amount={effectiveAmount} />
        </div>
      </div>
    </div>
  );
}